import { useEffect, useState } from 'react';

import { supabase } from '@/components/atomic-crm/providers/supabase/supabase';

import { juntarRisco, ordenarPorRisco, type RiscoFila } from './riscoFila';

/**
 * A fila de ações do dia — quem contatar, do mais urgente pro menos.
 *
 * Herdada do BlocoPrioridades quando o Início virou bento: a lógica é a mesma
 * (view `seller_action_queue`, agrupamento por pessoa), mas agora ela vira um
 * HOOK porque dois lugares precisam dela ao mesmo tempo — o card "Fila de
 * hoje" e os botões de ação do resumo, que mostram a contagem por tipo. Duas
 * buscas da mesma view na mesma tela seriam dois números que podem divergir.
 *
 * A view é security_invoker: o navegador lê direto e a RLS decide o escopo
 * (vendedor vê o dele, gerente vê o time). Não filtramos por usuário aqui —
 * seria reimplementar autorização no cliente.
 */

export type ItemFila = {
  kind: string;
  priority: number;
  contact_name: string | null;
  detail: string | null;
  amount: number | null;
  days_waiting: number | null;
  /** Data do evento (assembleia, p.ex.) — vira o selo de urgência da linha. */
  event_date: string | null;
  /** Dono da venda — é o que separa "tarefa minha" de "fale com o vendedor"
   *  na fila do gerente (a RLS entrega o time cru; a leitura é nossa). */
  sales_person_id: number | null;
  /** A view já aponta pro registro que originou a ação — ver migration 89. */
  ref_table: string | null;
  ref_id: string | null;
  /** Só em cobrança: risco de exclusão/cancelamento pelas regras simples da
   *  view (migration 219 — ver riscoFila.ts). Nulo nos outros tipos. */
  risco?: RiscoFila | null;
  /** Só em cobrança de cota NÃO contemplada: parcelas que faltam para as 7 em
   *  aberto que excluem do grupo (Cl. 43ª). Nulo para contemplado. */
  parcelas_ate_exclusao?: number | null;
};

export type ItemAgrupado = ItemFila & { quantas: number };

export const ROTULO_FILA: Record<string, string> = {
  cobranca_inadimplente: 'Cobrança',
  lance_assembleia: 'Lance',
  venda_pendente: 'Pagamento',
  followup_proposta: 'Follow-up',
};

/** A ação que a linha pede, no botão à direita. */
export const ACAO_FILA: Record<string, string> = {
  cobranca_inadimplente: 'Abrir cobrança',
  lance_assembleia: 'Definir lance',
  venda_pendente: 'Regularizar',
  followup_proposta: 'Ligar',
};

/** Rota do botão de ação POR TIPO (a linha individual tem destino próprio). */
export const ROTA_FILA: Record<string, string> = {
  cobranca_inadimplente: '/inadimplentes',
  lance_assembleia: '/lances',
  venda_pendente: '/vendas?tab=pending',
  followup_proposta: '/propostas',
};

/** O que se conta quando a pessoa aparece mais de uma vez, por tipo de ação. */
const PLURAL: Record<string, string> = {
  cobranca_inadimplente: 'contratos',
  lance_assembleia: 'lances',
  venda_pendente: 'cotas',
  followup_proposta: 'propostas',
};

export const pluralFila = (kind: string) => PLURAL[kind] ?? 'itens';

/**
 * Teto de linhas trazidas. A fila inteira da casa tem ~86 hoje; 1000 dá folga
 * de dez vezes. Se um dia bater no teto, a contagem por tipo passa a ser da
 * amostra e não do total — por isso o aviso no console em vez de silêncio.
 */
const TETO = 1000;

const COLUNAS =
  'kind, priority, contact_name, detail, amount, days_waiting, event_date, sales_person_id, ref_table, ref_id';
/** Risco de exclusão (migration 219) — pedido à parte, ver o fallback abaixo. */
const COLUNAS_RISCO = 'risco, parcelas_ate_exclusao';

/**
 * Pra onde a linha leva.
 *
 * Quando a linha agrupa várias (o cliente com sete contratos em atraso), abrir
 * uma das sete seria escolher arbitrariamente qual — vai pra lista já filtrada
 * nessa pessoa.
 */
export const destinoDaLinha = (i: ItemAgrupado): string => {
  const nome = encodeURIComponent(i.contact_name ?? '');

  // Lance se decide em Contemplação, não na venda: é lá que estão o motor, a
  // assembleia e a recomendação. A busca já chega filtrada no consorciado.
  if (i.kind === 'lance_assembleia') return `/lances?q=${nome}`;

  if (i.quantas > 1) {
    if (i.kind === 'cobranca_inadimplente') return `/inadimplentes?q=${nome}`;
    if (i.kind === 'followup_proposta') return `/propostas?q=${nome}`;
    if (i.kind === 'venda_pendente') return `/vendas?tab=pending&q=${nome}`;
    return `/vendas?q=${nome}`;
  }

  if (i.ref_table === 'proposal_simulations') return `/propostas?q=${nome}`;
  if (i.ref_table === 'servopa_vendas' && i.ref_id) return `/vendas/${i.ref_id}`;
  return `/contacts?q=${nome}`;
};

/**
 * Uma PESSOA por linha, por tipo de ação: você liga uma vez pra ela, não
 * quatro. A chave inclui o TIPO — "cobrar o Fulano" e "follow-up da proposta
 * do Fulano" são dois telefonemas diferentes.
 */
const agruparPorPessoa = (itens: ItemFila[]): ItemAgrupado[] => {
  const porChave = new Map<string, ItemAgrupado>();
  for (const i of itens) {
    const chave = `${i.kind}|${(i.contact_name ?? '').trim().toUpperCase()}`;
    const atual = porChave.get(chave);
    if (atual) {
      atual.amount = (atual.amount ?? 0) + Number(i.amount ?? 0);
      atual.days_waiting = Math.max(atual.days_waiting ?? 0, i.days_waiting ?? 0);
      // O evento mais PRÓXIMO manda no selo — é ele que define a urgência.
      if (i.event_date && (!atual.event_date || i.event_date < atual.event_date)) {
        atual.event_date = i.event_date;
      }
      // O contrato mais arriscado da pessoa manda no selo de risco.
      Object.assign(atual, juntarRisco(atual, i));
      atual.quantas += 1;
    } else {
      porChave.set(chave, { ...i, amount: i.amount == null ? null : Number(i.amount), quantas: 1 });
    }
  }
  // A lista já veio ordenada (prioridade, risco); o Map preserva a inserção.
  return [...porChave.values()];
};

/**
 * @param escopoIds sales_person_id que a fila deve mostrar. `null` = sem
 *   filtro (super admin vendo a casa inteira). A RLS já corta o que a pessoa
 *   pode ler; este filtro é o recorte de QUEM se está olhando, que é outra
 *   coisa — sem ele, escolher um especialista no topo continuava mostrando a
 *   fila da North toda, que foi exatamente o defeito relatado.
 */
export const useFilaDoDia = (escopoIds: number[] | null = null) => {
  const [itens, setItens] = useState<ItemAgrupado[] | null>(null);
  const [porTipo, setPorTipo] = useState<Record<string, number>>({});
  // id → nome, pra fila do gerente dizer "Fale com {vendedor}". A tabela
  // users é aberta a todo autenticado (hierarquia, migration 66).
  const [nomesVendedores, setNomesVendedores] = useState<Map<number, string>>(new Map());

  useEffect(() => {
    let ativo = true;
    (async () => {
      // Duas leituras da mesma view: a lista é o topo da fila, a contagem é a
      // fila INTEIRA. Mostrar "6" nos botões porque a lista mostra 6 seria
      // dizer que a fila tem 6 — e ela pode ter sessenta.
      // UMA consulta só. Antes eram duas sobre a MESMA view: uma para listar e
      // outra só para contar por tipo. A view é cara (~240 ms de execução e
      // ~96 ms só de planejamento em produção, por ser um encadeamento
      // profundo) e a fila inteira cabe em dezenas de linhas — pagar duas
      // vezes por isso era o que deixava o Início lento.
      const consulta = (colunas: string) => {
        const base = supabase
          .from('seller_action_queue')
          .select(colunas)
          .order('priority', { ascending: true })
          .order('days_waiting', { ascending: false, nullsFirst: false })
          .limit(TETO);
        return escopoIds ? base.in('sales_person_id', escopoIds) : base;
      };

      const [primeira, usuarios] = await Promise.all([
        consulta(`${COLUNAS}, ${COLUNAS_RISCO}`),
        supabase.from('users').select('id, name'),
      ]);
      // O web publica a cada push; a migration 219 (colunas de risco) é
      // aplicada à mão. Se o web chegar antes, pedir coluna que não existe
      // derruba a consulta e a fila some inteira — então tenta de novo sem o
      // risco. Custa uma consulta a mais só nessa janela.
      const tudo = primeira.error ? await consulta(COLUNAS) : primeira;
      if (!ativo) return;
      // Dentro da mesma prioridade, risco ALTO de exclusão vem na frente; o
      // resto segue a ordem do banco (ver ordenarPorRisco). No cliente porque
      // o PostgREST não ordena por expressão, e a fila inteira cabe no TETO.
      const linhas = tudo.error
        ? []
        : ordenarPorRisco((tudo.data ?? []) as unknown as ItemFila[]);
      if (linhas.length >= TETO) {
        console.warn('[fila do dia] teto de linhas atingido: a contagem por tipo vira amostra');
      }
      // A fila INTEIRA: o "Ver as N pendências" lista todas (pedido do Pedro,
      // 28/10/2026 — antes vinham só as 60 primeiras linhas).
      setItens(agruparPorPessoa(linhas));
      setNomesVendedores(
        new Map(
          ((usuarios.data ?? []) as { id: number; name: string | null }[])
            .filter((u) => u.name)
            .map((u) => [Number(u.id), u.name as string]),
        ),
      );
      const contagem: Record<string, number> = {};
      for (const r of linhas) {
        contagem[r.kind] = (contagem[r.kind] ?? 0) + 1;
      }
      setPorTipo(contagem);
    })();
    return () => {
      ativo = false;
    };
    // A lista de ids entra como texto: array novo a cada render com o mesmo
    // conteúdo não pode disparar consulta.
  }, [escopoIds ? escopoIds.join(',') : null]);

  // Uma pendência = uma linha do "Ver as N pendências" (pessoa + tipo), para o
  // número do atalho "Fila de hoje" e o da lista serem o mesmo.
  const totalFila = itens?.length ?? 0;
  return { itens, porTipo, totalFila, nomesVendedores, carregando: itens === null };
};
